import React from 'react';
import './DollarInputField.css';

const DollarInputField = ({title, value, setTo, locked, width=100, globalUpdate=null, complete=null}) => {
    const globalUpdateCall = () => {
        if(globalUpdate) globalUpdate();
    };

    // Helper function to get validation class
    const getValidationClass = () => {
        if (complete === null) return '';
        if (complete === false) return 'incomplete';
        if (complete === true) return 'complete';
        return '';
    };

    // Helper function to get validation indicator
    const getValidationIndicator = () => {
        if (complete === null) return null;
        if (complete === false) return <span className='validation-indicator error'>*</span>;
        if (complete === true) return <span className='validation-indicator success'>✓</span>;
        return null;
    };

    // Format value for display with commas
    const formatDisplayValue = (val) => {
        if (!val || val === '') return '';
        const numericValue = parseFloat(val);
        return isNaN(numericValue) ? val : numberWithCommas(numericValue.toFixed(2));
    };

    // Handle input change - remove commas for internal storage
    const handleInputChange = (e) => {
        const inputValue = e.target.value;
        // Remove commas and store clean numeric value, preserve decimal point
        const cleanValue = inputValue.replace(/,/g, '');
        
        // Only update if it's a valid number format (including partial decimals)
        if (cleanValue === '' || /^-?\d*\.?\d*$/.test(cleanValue)) {
            setTo(cleanValue);
        }
    };

    // Format input value for display (with commas)
    const formatInputValue = (val) => {
        if (!val || val === '') return '';
        
        // If the value ends with a decimal point, preserve it
        if (val.toString().endsWith('.')) {
            const numericPart = val.toString().slice(0, -1);
            const numericValue = parseFloat(numericPart);
            return isNaN(numericValue) ? val : numberWithCommas(numericValue) + '.';
        }
        
        const numericValue = parseFloat(val);
        if (isNaN(numericValue)) return val;
        
        // Check if it's a whole number or has decimals
        const hasDecimals = val.toString().includes('.') && val.toString().split('.')[1] !== '';
        if (hasDecimals) {
            // Preserve the original decimal places for display during editing
            const decimalPlaces = val.toString().split('.')[1].length;
            return numberWithCommas(numericValue.toFixed(decimalPlaces));
        }
        
        return numberWithCommas(numericValue);
    };

    // Number formatting function
    const numberWithCommas = (number) => {
        const parts = number.toString().split('.');
        parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ",");
        return parts.join('.');
    };

    return(
        <div className='dollar-input-field-container'>
            <div className='dollar-input-field-label'>
                {title}:
                {getValidationIndicator()}
            </div>
            <div className='dollar-input-field-wrapper'>
                <div className='dollar-input-field-symbol'>$</div>
                {locked ? (
                    <div className={`dollar-input-field-display ${getValidationClass()}`} style={{width:`${width}px`}}>
                        {formatDisplayValue(value)}
                    </div>
                ) : (
                    <input 
                        className={`dollar-input-field-input ${getValidationClass()}`}
                        style={{width:`${width}px`}} 
                        onBlur={() => globalUpdateCall()} 
                        type="text" 
                        value={formatInputValue(value)} 
                        onChange={handleInputChange}
                        onKeyDown={(e) => {
                            // Allow: backspace, delete, tab, escape, enter
                            if ([46, 8, 9, 27, 13].indexOf(e.keyCode) !== -1 ||
                                // Allow: Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X
                                (e.keyCode === 65 && e.ctrlKey === true) ||
                                (e.keyCode === 67 && e.ctrlKey === true) ||
                                (e.keyCode === 86 && e.ctrlKey === true) ||
                                (e.keyCode === 88 && e.ctrlKey === true) ||
                                // Allow: home, end, left, right
                                (e.keyCode >= 35 && e.keyCode <= 39)) {
                                return;
                            }
                            
                            // Allow decimal point (both numpad and regular)
                            if (e.keyCode === 190 || e.keyCode === 110) {
                                // Prevent multiple decimal points
                                const currentValue = e.target.value.replace(/,/g, '');
                                if (currentValue.includes('.')) {
                                    e.preventDefault();
                                }
                                return;
                            }
                            
                            // Ensure that it is a number
                            if ((e.shiftKey || (e.keyCode < 48 || e.keyCode > 57)) && 
                                (e.keyCode < 96 || e.keyCode > 105)) {
                                e.preventDefault();
                            }
                        }}
                        placeholder="0"
                    />
                )}
            </div>
        </div>
    );
};

export default DollarInputField;
